import { Fragment } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, Network, Truck, BarChart3, Calendar, FileText, Database, ScanSearch } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useSimulation } from '@/hooks/useSimulation';
import { t } from '@/i18n';
import { initialLoadings, countByState } from '@/data/arrivalsData';
import { isInSection } from './subViews';

// Arrivals badge: today's loadings without any information from the carrier.
const arrivalsWithoutInfo = countByState(initialLoadings)['no-info'];

const items = [
  { name: t('nav.trucks'), href: '/kamiony', icon: Truck, countKey: 'trucks' },
  { name: t('nav.stats'), href: '/statistiky', icon: BarChart3 },
  // Concepts: ideas and options, shown muted after a divider.
  { name: t('nav.dashboard'), href: '/dashboard', icon: LayoutDashboard, concept: true },
  { name: t('nav.supplyChain'), href: '/supply-chain', icon: Network, concept: true },
  { name: t('nav.planning'), href: '/planovani', icon: Calendar, concept: true },
  { name: t('nav.orders'), href: '/objednavky', icon: FileText, countKey: 'orders', concept: true },
  { name: t('nav.sapIntegration'), href: '/sap', icon: Database, concept: true },
  { name: t('nav.branchDetection'), href: '/detekce-vetvi', icon: ScanSearch, concept: true },
];

export const BottomNav = () => {
  const location = useLocation();
  const { orders } = useSimulation();

  const counts = {
    trucks: arrivalsWithoutInfo,
    orders: orders.filter(o => o.status === 'pending').length,
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 lg:hidden z-40 bg-white/95 backdrop-blur-md border-t border-border pb-[env(safe-area-inset-bottom)]">
      <div className="flex items-center justify-around h-16 px-2">
        {items.map((item, i) => {
          const isActive = isInSection(location.pathname, item.href);
          const count = item.countKey ? counts[item.countKey] : 0;
          const firstConcept = item.concept && !items[i - 1]?.concept;
          return (
            <Fragment key={item.name}>
              {firstConcept && <div className="w-px h-8 bg-border shrink-0" aria-hidden="true" />}
              <Link
                to={item.href}
                className={cn(
                  'flex flex-col items-center justify-center gap-0.5 flex-1 min-w-0 py-2 transition-colors',
                  isActive ? 'text-[hsl(23,81%,52%)]' : 'text-muted-foreground',
                  item.concept && !isActive && 'opacity-60',
                )}
              >
                <div className="relative">
                  <item.icon className={cn('h-5 w-5', isActive && 'stroke-[2.5]')} />
                  {count > 0 && (
                    <span className="absolute -top-1.5 -right-2 min-w-[14px] h-[14px] text-[9px] font-bold bg-[hsl(23,81%,52%)] text-white rounded-full flex items-center justify-center px-1">
                      {count > 99 ? '99+' : count}
                    </span>
                  )}
                </div>
                <span className="text-[10px] font-medium truncate max-w-full px-0.5">
                  {item.name}
                </span>
              </Link>
            </Fragment>
          );
        })}
      </div>
    </nav>
  );
};
